import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** `unknown` breaks named-export lazy loads (e.g. wrapping `mod.Foo` as default). */
type Factory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

const RETRY_DELAY_MS = 450;
const MAX_ATTEMPTS = 2;

/**
 * React.lazy wrapper: retries the dynamic import once after a short delay.
 * Helps flaky networks and brief CDN hiccups. Stale chunk hashes (404 after
 * deploy) still fail after retry — ErrorBoundary / chunk recovery should reload.
 */
export function lazyImport<T extends ComponentType<any>>(
  factory: Factory<T>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        return await factory();
      } catch (e) {
        lastError = e;
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
      }
    }
    throw lastError;
  });
}
